// PDF brochure generator for course flyers - client-side using jsPDF
import { jsPDF } from "jspdf";
import { siteInfo, careerOutcomes } from "../data/mock";

// Brand colors
const BRAND = [13, 76, 43]; // #0d4c2b
const BRAND_LIGHT = [232, 243, 236]; // #e8f3ec
const TEXT_DARK = [10, 40, 24];
const TEXT_MUTED = [90, 110, 100];
const WHITE = [255, 255, 255];

const PAGE_W = 210; // A4 mm
const PAGE_H = 297;
const MARGIN = 15;
const CONTENT_W = PAGE_W - 2 * MARGIN;

const setFillRGB = (doc, [r, g, b]) => doc.setFillColor(r, g, b);
const setTextRGB = (doc, [r, g, b]) => doc.setTextColor(r, g, b);
const setDrawRGB = (doc, [r, g, b]) => doc.setDrawColor(r, g, b);

const drawHeader = (doc, course) => {
  // Green header band
  setFillRGB(doc, BRAND);
  doc.rect(0, 0, PAGE_W, 55, "F");

  // Small decorative circles (dots pattern)
  setFillRGB(doc, [255, 255, 255]);
  doc.setGState(new doc.GState({ opacity: 0.08 }));
  for (let x = 10; x < PAGE_W; x += 12) {
    for (let y = 5; y < 55; y += 12) {
      doc.circle(x, y, 0.8, "F");
    }
  }
  doc.setGState(new doc.GState({ opacity: 1 }));

  // Association logo circle (top left)
  setFillRGB(doc, WHITE);
  doc.circle(MARGIN + 6, 15, 5, "F");
  setTextRGB(doc, BRAND);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("APF", MARGIN + 6, 16, { align: "center" });

  // Association name
  setTextRGB(doc, WHITE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("ASSOCIAZIONE PROGETTO FUTURO", MARGIN + 15, 13);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("Corsi di formazione professionale · Vibo Valentia", MARGIN + 15, 18);

  // Course category badge
  if (course.tag) {
    setFillRGB(doc, WHITE);
    doc.setGState(new doc.GState({ opacity: 0.15 }));
    doc.roundedRect(PAGE_W - MARGIN - 45, 10, 45, 8, 4, 4, "F");
    doc.setGState(new doc.GState({ opacity: 1 }));
    setTextRGB(doc, WHITE);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(course.tag.toUpperCase(), PAGE_W - MARGIN - 22.5, 15.5, { align: "center" });
  }

  // Course title
  setTextRGB(doc, WHITE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  const titleLines = doc.splitTextToSize(course.title, CONTENT_W);
  doc.text(titleLines, MARGIN, 34);

  // Full title
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  const subLines = doc.splitTextToSize(course.fullTitle, CONTENT_W);
  doc.text(subLines, MARGIN, 44);
};

const drawStatsBox = (doc, course, y) => {
  // Four stat boxes: duration, theory, practice, price
  const stats = [
    { label: "DURATA", value: course.duration },
    { label: "TEORIA", value: course.theory },
    { label: "PRATICA", value: course.practice },
    { label: "COSTO", value: course.price },
  ];
  const boxW = (CONTENT_W - 9) / 4; // 3mm gap
  stats.forEach((s, i) => {
    const x = MARGIN + i * (boxW + 3);
    setFillRGB(doc, BRAND_LIGHT);
    doc.roundedRect(x, y, boxW, 20, 3, 3, "F");
    setTextRGB(doc, TEXT_MUTED);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.text(s.label, x + boxW / 2, y + 6, { align: "center" });
    setTextRGB(doc, BRAND);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    const valLines = doc.splitTextToSize(String(s.value), boxW - 4);
    doc.text(valLines[0] || "", x + boxW / 2, y + 14, { align: "center" });
  });
  return y + 25;
};

const drawSectionTitle = (doc, text, y) => {
  setTextRGB(doc, BRAND);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(text, MARGIN, y);
  setDrawRGB(doc, BRAND);
  doc.setLineWidth(0.6);
  doc.line(MARGIN, y + 1.5, MARGIN + 20, y + 1.5);
  return y + 8;
};

const drawParagraph = (doc, text, y, fontSize = 10, color = TEXT_DARK) => {
  setTextRGB(doc, color);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(fontSize);
  const lines = doc.splitTextToSize(text, CONTENT_W);
  doc.text(lines, MARGIN, y);
  return y + lines.length * (fontSize * 0.42) + 3;
};

const drawModule = (doc, module, index, y) => {
  // Number circle
  setFillRGB(doc, BRAND);
  doc.circle(MARGIN + 4, y + 3, 3.5, "F");
  setTextRGB(doc, WHITE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text(String(index + 1), MARGIN + 4, y + 4, { align: "center" });

  // Title + detail
  setTextRGB(doc, TEXT_DARK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  const titleLines = doc.splitTextToSize(module.title, CONTENT_W - 12);
  doc.text(titleLines, MARGIN + 11, y + 3);

  let nextY = y + 3 + titleLines.length * 4.5;
  setTextRGB(doc, TEXT_MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const detailLines = doc.splitTextToSize(module.detail, CONTENT_W - 12);
  doc.text(detailLines, MARGIN + 11, nextY);
  return nextY + detailLines.length * 4 + 4;
};

const drawFooter = (doc) => {
  const y = PAGE_H - 25;
  setFillRGB(doc, BRAND);
  doc.rect(0, y, PAGE_W, 25, "F");

  setTextRGB(doc, WHITE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("PROGETTIAMO INSIEME IL TUO FUTURO", MARGIN, y + 8);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Telefono: ${siteInfo.phone}`, MARGIN, y + 15);
  doc.text(`Email: ${siteInfo.email}`, MARGIN, y + 20);

  // Right side
  doc.setFontSize(9);
  doc.text(siteInfo.address, PAGE_W - MARGIN, y + 15, { align: "right" });
  setTextRGB(doc, [200, 230, 210]);
  doc.setFontSize(8);
  doc.text("associazioneprogettofuturo.com", PAGE_W - MARGIN, y + 20, { align: "right" });
};

const drawCheckList = (doc, items, y) => {
  items.forEach((item) => {
    // Green circle
    setFillRGB(doc, BRAND_LIGHT);
    doc.circle(MARGIN + 3, y + 1, 2.5, "F");
    setDrawRGB(doc, BRAND);
    doc.setLineWidth(0.8);
    // simple check mark
    doc.line(MARGIN + 2, y + 1, MARGIN + 2.8, y + 1.8);
    doc.line(MARGIN + 2.8, y + 1.8, MARGIN + 4.5, y + 0.2);

    setTextRGB(doc, TEXT_DARK);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(item, MARGIN + 9, y + 2);
    y += 6;
  });
  return y + 2;
};

export const generateCoursePDF = (course) => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });

  drawHeader(doc, course);

  let y = 66;
  y = drawStatsBox(doc, course, y);

  // Description
  y = drawSectionTitle(doc, "IL CORSO", y + 2);
  y = drawParagraph(doc, course.description, y);

  // Programma
  y = drawSectionTitle(doc, "PROGRAMMA FORMATIVO", y + 3);
  course.modules.forEach((m, i) => {
    // Check if we need a new page
    if (y > 235) {
      drawFooter(doc);
      doc.addPage();
      drawHeader(doc, course);
      y = 66;
    }
    y = drawModule(doc, m, i, y);
  });

  // Career outcomes
  const outcomes = careerOutcomes[course.slug] || [];
  if (outcomes.length > 0) {
    if (y > 210) {
      drawFooter(doc);
      doc.addPage();
      drawHeader(doc, course);
      y = 66;
    }
    y = drawSectionTitle(doc, "SBOCCHI PROFESSIONALI", y + 3);
    y = drawCheckList(doc, outcomes, y);
  }

  // Qualifica
  if (y > 220) {
    drawFooter(doc);
    doc.addPage();
    drawHeader(doc, course);
    y = 66;
  }
  y = drawSectionTitle(doc, "QUALIFICA FINALE", y + 3);
  setFillRGB(doc, BRAND_LIGHT);
  const qualLines = doc.splitTextToSize(course.qualification, CONTENT_W - 8);
  const boxH = qualLines.length * 5 + 6;
  doc.roundedRect(MARGIN, y - 3, CONTENT_W, boxH, 3, 3, "F");
  setTextRGB(doc, TEXT_DARK);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(qualLines, MARGIN + 4, y + 2);
  y += boxH + 3;

  // CTA box (only if space available)
  if (y < 240) {
    setFillRGB(doc, BRAND);
    doc.roundedRect(MARGIN, y, CONTENT_W, 20, 3, 3, "F");
    setTextRGB(doc, WHITE);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("Vuoi maggiori informazioni?", PAGE_W / 2, y + 8, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`Chiama ${siteInfo.phone} o visita il nostro sito`, PAGE_W / 2, y + 14.5, { align: "center" });
  }

  drawFooter(doc);

  return doc;
};

export const downloadCoursePDF = (course) => {
  const doc = generateCoursePDF(course);
  const filename = `${course.slug || "corso"}-progetto-futuro.pdf`;
  doc.save(filename);
};

export const shareOnWhatsApp = (course) => {
  const text = encodeURIComponent(
    `Guarda questo corso di Associazione Progetto Futuro:\n\n` +
    `📚 *${course.title}*\n` +
    `${course.fullTitle}\n\n` +
    `⏱ Durata: ${course.duration}\n` +
    `💳 Pagamento: ${course.price}\n\n` +
    `${course.shortDescription}\n\n` +
    `Info: ${siteInfo.phone}\n` +
    `Scopri di più sul sito!`
  );
  window.open(`https://wa.me/?text=${text}`, "_blank");
};
